import { useEffect, useRef, useState, ReactNode, useCallback } from 'react';
import { Box, Stack, Tabs, Tab } from '@mui/material';
import { colors } from '@sweep-io/sweep-design';
import { PAGE_SIDE_PADDING } from '../../../constants';
import { useExpandedMode } from '../../pages/configuration-canvas/panels-reserve-space/useExpandedMode';
import { TabLabel } from '../tabs/TabLabel';
import { TabListItem } from '../tabs/TabListItem';

export interface DocumentationTab {
  id: string;
  label: string;
  isNotRemovable?: boolean;
}

export interface DocumentationTabsLayoutProps {
  tabs: DocumentationTab[];
  activeTab?: string;
  defaultTabId?: string;
  onTabChange: (tabId: string) => void;
  onRemoveTab?: (tabId: string) => void;
  formatLabel?: (label: string) => string;
  renderActionSlot?: (compact: boolean) => ReactNode;
  trailingSlot?: ReactNode;
  bottomSlot?: ReactNode;
  children: ReactNode;
  onMount?: () => void;
}

export const DocumentationTabsLayout = ({
  tabs,
  activeTab,
  defaultTabId,
  onTabChange,
  onRemoveTab,
  formatLabel = (label) => label,
  renderActionSlot,
  trailingSlot,
  bottomSlot,
  children,
  onMount,
}: DocumentationTabsLayoutProps) => {
  const { isExpandedMode } = useExpandedMode();
  const [isCompactMode, setIsCompactMode] = useState(false);

  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const actionSlotRef = useRef<HTMLDivElement>(null);
  const trailingSlotRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = tabsContainerRef.current;
    const actionEl = actionSlotRef.current;
    const trailingEl = trailingSlotRef.current;

    if (!container || !actionEl || !trailingEl) return;

    const checkSpacing = () => {
      const actionRect = actionEl.getBoundingClientRect();
      const trailingRect = trailingEl.getBoundingClientRect();
      const distance = trailingRect.left - actionRect.right;
      setIsCompactMode(distance < 100);
    };

    const resizeObserver = new ResizeObserver(checkSpacing);
    resizeObserver.observe(container);
    checkSpacing();

    return () => resizeObserver.disconnect();
  }, [tabs]);

  useEffect(() => {
    onMount?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRemoveTab = useCallback(
    (tabId: string) => {
      onRemoveTab?.(tabId);
    },
    [onRemoveTab],
  );

  if (!isExpandedMode) {
    if (activeTab) {
      return children;
    }

    return (
      <Stack
        sx={{
          height: '100%',
          overflow: 'hidden',
        }}
        data-testid="documentation-tabs"
      >
        {tabs.map((tab) => (
          <TabListItem
            key={tab.id}
            tab={tab}
            handleTabChange={onTabChange}
            onRemoveTab={handleRemoveTab}
            formatLabel={formatLabel}
          />
        ))}
        {bottomSlot && (
          <Stack
            sx={{
              flexDirection: 'row',
              position: 'sticky',
              bottom: 0,
              zIndex: 900,
              backgroundColor: colors.white,
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: 2,
            }}
          >
            {bottomSlot}
          </Stack>
        )}
      </Stack>
    );
  }

  return (
    <Stack
      sx={{
        height: '100%',
        overflow: 'hidden',
        mr: PAGE_SIDE_PADDING + 'px',
        ml: PAGE_SIDE_PADDING + 'px',
      }}
    >
      <Stack
        ref={tabsContainerRef}
        direction="row"
        alignItems="center"
        sx={{
          backgroundColor: colors.grey[100],
          borderRadius: '8px 8px 0 0',
          position: 'relative',
          zIndex: 1,
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '0',
            left: '0',
            right: '0',
            height: '1px',
            backgroundColor: colors.grey[120],
            zIndex: 0,
          },
        }}
      >
        <Tabs
          value={activeTab || tabs[0]?.id || defaultTabId || ''}
          onChange={(_, newValue) => onTabChange(newValue)}
          variant="standard"
          sx={{
            minHeight: '42px',
            width: 'fit-content',
            maxWidth: `calc(100% - ${isCompactMode ? '30px' : '180px'})`,
            '& .MuiTabs-flexContainer': {
              gap: 0,
              justifyContent: 'flex-start',
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            },
            '& .MuiTabs-scroller': {
              overflow: 'visible !important',
            },
            '& .MuiTab-root': {
              color: colors.grey[700],
              padding: '4px ',
              minWidth: 0,
              width: '185px',
              flexShrink: 1,
              overflow: 'visible',
              textAlign: 'left',
              border: `1px solid transparent`,
              borderBottom: `1px solid ${colors.grey[120]}`,
              position: 'relative',
              '& .MuiTab-wrapper': {
                width: '100%',
                overflow: 'visible',
                display: 'block',
              },
              '&.Mui-selected': {
                cursor: 'default',
                color: colors.blue[500],
                backgroundColor: colors.white,
                overflow: 'visible',
                border: `1px solid ${colors.grey[120]}`,
                borderBottom: 'none',
                borderRadius: '8px 8px 0 0',
                zIndex: 2,
                '& .tab-content': {
                  '&:after': {
                    backgroundColor: 'transparent',
                  },
                },
                '& .tab-content:hover': {
                  background: colors.white,
                },
              },
              '&:hover': {
                color: colors.grey[900],
                backgroundColor: colors.grey[50],
                '& .remove-metadata-button': {
                  display: 'inline-flex',
                },
              },
            },
          }}
        >
          {tabs.map((tab, index) => {
            const isOneBeforeSelected = index === tabs.findIndex((t) => t.id === activeTab) - 1;

            return (
              <Tab
                key={tab.id}
                label={
                  <TabLabel
                    title={formatLabel(tab.label)}
                    onRemove={tab.isNotRemovable ? undefined : () => handleRemoveTab(tab.id)}
                  >
                    {formatLabel(tab.label)}
                  </TabLabel>
                }
                value={tab.id}
                sx={{
                  '& .tab-content::after': {
                    backgroundColor: isOneBeforeSelected ? 'transparent' : colors.grey[120],
                  },
                }}
              />
            );
          })}
        </Tabs>

        {renderActionSlot && <Box ref={actionSlotRef}>{renderActionSlot(isCompactMode)}</Box>}
        <Box sx={{ flex: 1 }} />
        <Box ref={trailingSlotRef}>{trailingSlot}</Box>
      </Stack>
      <Stack
        sx={{
          flex: 1,
          overflow: 'hidden',
          borderLeft: `1px solid ${colors.grey[120]}`,
          borderRight: `1px solid ${colors.grey[120]}`,
          borderBottom: `1px solid ${colors.grey[120]}`,
          borderRadius: '0 0 8px 8px',
          backgroundColor: colors.white,
          position: 'relative',
          width: '100%',
        }}
      >
        {children}
      </Stack>
    </Stack>
  );
};
